import { createRng } from './rng';
import { nextSeatClockwise } from './rules';
import type { BlindLevel, Player, PlayerId, Seat, TournamentState } from './types';

export const INITIAL_STACK = 2000;
export const HANDS_PER_LEVEL = 5;
export const REBUY_CUTOFF_LEVEL = 3;
export const MAX_SEATS = 6;
export const MIN_PLAYERS = 2;

/**
 * Blind schedule per FIRST_INSTRUCTION.md §4.1 L178–L185.
 * BB-only ante from L5 (D1).
 */
export const BLIND_SCHEDULE: ReadonlyArray<BlindLevel> = [
  { level: 1, sb: 10, bb: 20, ante: 0 },
  { level: 2, sb: 15, bb: 30, ante: 0 },
  { level: 3, sb: 25, bb: 50, ante: 0 },
  { level: 4, sb: 50, bb: 100, ante: 0 },
  { level: 5, sb: 75, bb: 150, ante: 150 },
  { level: 6, sb: 100, bb: 200, ante: 200 },
  { level: 7, sb: 150, bb: 300, ante: 300 },
  { level: 8, sb: 200, bb: 400, ante: 400 },
  { level: 9, sb: 300, bb: 600, ante: 600 },
  { level: 10, sb: 400, bb: 800, ante: 800 },
  { level: 11, sb: 500, bb: 1000, ante: 1000 },
  { level: 12, sb: 700, bb: 1400, ante: 1400 },
  { level: 13, sb: 1000, bb: 2000, ante: 2000 },
  { level: 14, sb: 1500, bb: 3000, ante: 3000 },
  { level: 15, sb: 2000, bb: 4000, ante: 4000 },
];

export function getBlindsForHand(handNumber: number): BlindLevel {
  if (handNumber < 1) throw new RangeError(`handNumber must be >= 1, got ${handNumber}`);
  const idx = Math.min(Math.floor((handNumber - 1) / HANDS_PER_LEVEL), BLIND_SCHEDULE.length - 1);
  return BLIND_SCHEDULE[idx] as BlindLevel;
}

export function isRebuyOpen(handNumber: number): boolean {
  return getBlindsForHand(handNumber).level <= REBUY_CUTOFF_LEVEL;
}

export function canPlayerRebuy(player: Player, handNumber: number): boolean {
  return (
    !player.eliminated &&
    !player.rebuyUsed &&
    player.stack < INITIAL_STACK &&
    isRebuyOpen(handNumber)
  );
}

/** Top-up the player to the starting stack. Flags rebuy as used. */
export function applyRebuy(player: Player): Player {
  return {
    ...player,
    stack: INITIAL_STACK,
    rebuyUsed: true,
  };
}

/** Advance the button clockwise, skipping eliminated seats (forward-moving D2). */
export function advanceButton(currentButton: Seat, players: ReadonlyArray<Player>): Seat {
  const seats = players
    .filter((p) => !p.eliminated)
    .map((p) => p.seat)
    .sort((a, b) => a - b);
  if (seats.length === 0) {
    throw new Error('advanceButton: no active seats');
  }
  if (seats.length === 1) return seats[0] as Seat;
  return nextSeatClockwise(currentButton, seats);
}

/** Top-3 prize distribution by share (§4.1 L187: 50/30/20). */
export function computePrizeShares(prizePool: number): {
  rank1: number;
  rank2: number;
  rank3: number;
} {
  const r1 = Math.floor(prizePool * 0.5);
  const r2 = Math.floor(prizePool * 0.3);
  // Remainder (due to flooring) goes to rank1 per standard convention.
  const r3 = prizePool - r1 - r2 - Math.floor(prizePool * 0.2);
  return {
    rank1: r1 + r3,
    rank2: r2,
    rank3: Math.floor(prizePool * 0.2),
  };
}

/**
 * Initialize a tournament. Places players at seats 0..N-1 in input order.
 */
export function initTournament(params: {
  seed: string;
  playerIds: ReadonlyArray<PlayerId>;
}): TournamentState {
  const { seed, playerIds } = params;
  if (playerIds.length < MIN_PLAYERS || playerIds.length > MAX_SEATS) {
    throw new RangeError(
      `Tournament needs ${MIN_PLAYERS}-${MAX_SEATS} players, got ${playerIds.length}`,
    );
  }
  const players: Player[] = playerIds.map((id, i) => ({
    id,
    seat: i as Seat,
    stack: INITIAL_STACK,
    folded: false,
    allIn: false,
    handContribution: 0,
    streetContribution: 0,
    rebuyUsed: false,
    eliminated: false,
  }));
  // Deterministic seat-draw: the first dealer button is picked from the
  // session seed, matching the "high card" ritual in real tournaments. This
  // removes the bias where hero (p0) always started on the button.
  const buttonRng = createRng(seed, 'initial-button');
  const initialButton = buttonRng.int(playerIds.length) as Seat;
  return {
    seed,
    handNumber: 0,
    currentLevel: BLIND_SCHEDULE[0] as BlindLevel,
    players,
    button: initialButton,
    prizePool: playerIds.length * INITIAL_STACK,
    prizePoolFrozen: false,
    rebuysLocked: false,
    eliminationOrder: [],
    finished: false,
  };
}

/** Reset per-hand player flags before starting the next hand. */
export function resetPlayersForHand(players: ReadonlyArray<Player>): Player[] {
  return players.map((p) => ({
    ...p,
    holeCards: undefined,
    folded: false,
    allIn: false,
    handContribution: 0,
    streetContribution: 0,
  }));
}

/**
 * Process end-of-hand bookkeeping:
 * - flag newly-eliminated players (stack=0 AND rebuy ineligible)
 * - record elimination order with same-hand tie-break by start-of-hand stack (D6)
 * - freeze prize pool when leaving L3 for L4
 * - mark finished when only 1 non-eliminated player remains
 */
export function applyHandEnd(
  state: TournamentState,
  finalPlayers: ReadonlyArray<Player>,
  startOfHandStacks: Readonly<Record<PlayerId, number>>,
): TournamentState {
  const nextHandNumber = state.handNumber + 1;
  // Level of the hand that just played (derived from handNumber, not stale state.currentLevel).
  const justPlayedLevel = getBlindsForHand(nextHandNumber).level;
  const upcomingLevel = getBlindsForHand(nextHandNumber + 1).level;
  const willLockRebuys =
    justPlayedLevel <= REBUY_CUTOFF_LEVEL && upcomingLevel > REBUY_CUTOFF_LEVEL;

  // Players who just busted this hand.
  const newlyBusted: Player[] = [];
  const merged: Player[] = finalPlayers.map((p) => {
    if (p.eliminated) return p;
    if (p.stack === 0 && (p.rebuyUsed || !isRebuyOpen(nextHandNumber))) {
      const eliminated: Player = {
        ...p,
        eliminated: true,
        eliminatedAtHand: state.handNumber + 1,
      };
      newlyBusted.push(eliminated);
      return eliminated;
    }
    return p;
  });

  // Sort same-hand busts by their start-of-hand stacks ascending (smaller = out first).
  newlyBusted.sort((a, b) => (startOfHandStacks[a.id] ?? 0) - (startOfHandStacks[b.id] ?? 0));
  const bustOrder = newlyBusted.map((p) => p.id);

  const survivors = merged.filter((p) => !p.eliminated);
  const finished = survivors.length <= 1;
  const button = finished ? state.button : advanceButton(state.button, merged);

  return {
    ...state,
    handNumber: state.handNumber + 1,
    currentLevel: getBlindsForHand(state.handNumber + 1),
    players: merged,
    button,
    prizePoolFrozen: state.prizePoolFrozen || willLockRebuys,
    rebuysLocked: state.rebuysLocked || willLockRebuys,
    eliminationOrder: [...state.eliminationOrder, ...bustOrder],
    finished,
    finishedAtHand: finished ? state.handNumber + 1 : state.finishedAtHand,
  };
}

export function isTournamentOver(state: TournamentState): boolean {
  return state.finished;
}

/**
 * Compute final standings once the tournament has finished. Returns ranks
 * ordered from 1st (winner) to last (first to bust).
 */
export function computeFinalStandings(state: TournamentState): Array<{
  rank: number;
  playerId: PlayerId;
  prize: number;
}> {
  if (!state.finished) {
    throw new Error('computeFinalStandings: tournament not finished');
  }
  const survivor = state.players.find((p) => !p.eliminated);
  if (!survivor) {
    throw new Error('computeFinalStandings: no survivor');
  }
  // rank 1 = survivor; rank 2 = last eliminated (tail of eliminationOrder); etc.
  const reversed = [...state.eliminationOrder].reverse();
  const ordered: PlayerId[] = [survivor.id, ...reversed];
  const { rank1, rank2, rank3 } = computePrizeShares(state.prizePool);
  return ordered.map((id, idx) => ({
    rank: idx + 1,
    playerId: id,
    prize: idx === 0 ? rank1 : idx === 1 ? rank2 : idx === 2 ? rank3 : 0,
  }));
}
